import React, { useState } from 'react';
import { Button, TextField, Typography, Link as MuiLink, IconButton } from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { signInWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from '../../firebase/firebase.js'; 
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MagnetLines from '../../components/magnetLines/MagnetLines';
import './authSass/Login.scss';
import Loader from '../../components/loader/Loader';
import { useSelector, useDispatch } from 'react-redux';
import { selectPreviousURL, CLEAR_PREVIOUS_URL } from '../../redux/slice/taskSlice';


const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const previousURL = useSelector(selectPreviousURL);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
 
  const ensureUserProfileInFirestore = async (user) => {
    const userDocRef = doc(db, "users", user.uid);
    let profileData = {};
   
    try {
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        profileData = userDocSnap.data();
      }
    } catch (error) {
      console.warn("Could not fetch user profile from Firestore (possibly offline or rules issue), proceeding with defaults:", error.message);
      
    }

    const updateData = {
      uid: user.uid,
      email: user.email,
      displayName: user.displayName || (user.email ? user.email.split('@')[0] : 'User'),
      lastLoginAt: serverTimestamp(),
    };

    if (!profileData.createdAt) { 
      updateData.createdAt = serverTimestamp(); 
    }

    
    if (!profileData.role) {
      updateData.role = "user"; 
    } else {
      updateData.role = profileData.role; 
    }

    
    await setDoc(userDocRef, updateData, { merge: true });
    console.log(`Firestore user profile ensured for UID ${user.uid}. Final Role: ${updateData.role}`);
  };


  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      await ensureUserProfileInFirestore(user); 

      toast.success("Login Successful!");
      if (previousURL) {
        navigate(previousURL);
        dispatch(CLEAR_PREVIOUS_URL());
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Email login error: ", error);
      toast.error(`Login failed: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  const handleGoogleLogin = async () => {
    setIsLoading(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      await ensureUserProfileInFirestore(user);
      toast.success("Google Login Successful!");
      if (previousURL) {
        navigate(previousURL);
        dispatch(CLEAR_PREVIOUS_URL());
      } else {
        navigate("/");
      }
    } catch (error) {
      console.error("Google login error: ", error);
      if (error.code === 'auth/popup-closed-by-user') {
        toast.info('Google login cancelled.');
      } else if (error.code === 'auth/cancelled-popup-request') {
        toast.error('Google login already in progress or blocked.');
      } else {
        toast.error(`Google login failed: ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };




 return (
    <>
      {isLoading && <Loader />}
      <div className="login-page-wrapper">
        <MagnetLines
          rows={9}
          columns={9}
          lineColor="rgba(80, 180, 255, 0.4)"
          lineWidth="0.6vmin"
          lineHeight="4vmin"
          baseAngle={-45}
          fullscreen={true}
        />

        <div className="login-container">
          <form onSubmit={handleEmailLogin} className="login-form"> {/* Use handleEmailLogin here */}
            <Typography variant="h4">Login</Typography>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              autoComplete="email"
            />
       <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                    sx={{ color: 'text.secondary' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
    ),
           
        }}
       />
           <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>Login</Button>
            <MuiLink component={RouterLink} to="/forgot-password" sx={{ mt: 1, textAlign: 'right', display: 'block' }}>
              Forgot Password?
            </MuiLink>

            <Typography variant="body2" sx={{ my: 2 }}>
              OR
            </Typography>

            <Button
              variant="outlined" 
              color="secondary" 
              fullWidth
              startIcon={<GoogleIcon />}
              onClick={handleGoogleLogin} 
            >
              Sign In with Google
            </Button>

            <Typography variant="body2" sx={{ mt: 2 }}>
              Don't have an account?{' '}
              <MuiLink component={RouterLink} to="/register">
                Register
              </MuiLink>
            </Typography>
          </form>
        </div>
      </div>
    </>
  );
};

export default Login;